import type { SxProps, Theme } from "@mui/material";

/**
 * Tokens y helpers de estilo específicos del rediseño de Login que no tienen
 * equivalente en `bpmV2Theme` (src/theme/Bpm.ts). Todo lo demás (colores de
 * texto, bordes, radios de botones/inputs, sombras) debe tomarse del tema via
 * `theme.palette`/`theme.shape`, no de este archivo.
 */

export const radius = {
    sm: 4,
    md: 8,
    lg: 12,
    pill: 999,
};

export const formColumnMaxWidth = 400;

export const otpBoxSx = (hasError: boolean): SxProps<Theme> => ({
    width: 46,
    height: 54,
    "& .MuiInputBase-root": {
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        padding: 0,
        borderRadius: `${radius.sm}px`,
        backgroundColor: hasError ? "error.light" : "#F9FAFB",
    },
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: hasError ? "error.main" : "#D5E3EC",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: hasError ? "error.main" : "secondary.main",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: hasError ? "error.main" : "secondary.main",
        boxShadow: hasError ? "none" : "0 0 0 3px rgba(82,140,214,0.20)",
    },
    "& .MuiInputBase-input": {
        padding: 0,
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        textAlign: "center",
        fontFamily: "'Outfit', system-ui, sans-serif",
        fontSize: "22px",
        fontWeight: 600,
        color: "text.primary",
    },
});

export const dividerLinksBlockSx: SxProps<Theme> = {
    mt: 3,
    pt: 2.5,
    borderTop: "1px solid",
    borderColor: "divider",
    display: "flex",
    flexDirection: "column",
    gap: 1.25,
    textAlign: "center",
};

export const linkSx: SxProps<Theme> = {
    color: "secondary.main",
    cursor: "pointer",
    fontWeight: 600,
    "&:hover": {
        color: "secondary.dark",
        textDecoration: "underline",
    },
};

export const primaryButtonSx: SxProps<Theme> = {
    height: 54,
    width: "100%",
    borderRadius: `${radius.md}px`,
    fontSize: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    "&:hover": {
        backgroundColor: "text.primary",
        filter: "brightness(0.92)",
        boxShadow: "none",
    },
};

// Gradiente y formas del panel de marca, tomados 1:1 del mockup de Claude Design.
export const brandPanelGradient = "linear-gradient(150deg, #04213f 0%, #062f5c 55%, #0a3a72 100%)";

export const brandFloatKeyframes = {
    "@keyframes lendiaFloat": {
        "0%, 100%": { transform: "translateY(0)" },
        "50%": { transform: "translateY(-14px)" },
    },
    "@keyframes lendiaRise": {
        from: { opacity: 0, transform: "translateY(14px)" },
        to: { opacity: 1, transform: "translateY(0)" },
    },
};

export const brandShapes: Array<{ sx: SxProps<Theme> }> = [
    {
        sx: {
            position: "absolute",
            top: "-120px",
            right: "-100px",
            width: 440,
            height: 440,
            borderRadius: "50%",
            background: "radial-gradient(circle at 30% 30%, rgba(82,140,214,0.45), transparent 68%)",
        },
    },
    {
        sx: {
            position: "absolute",
            bottom: "-160px",
            left: "-120px",
            width: 520,
            height: 520,
            borderRadius: "50%",
            background: "radial-gradient(circle at 60% 40%, rgba(163,212,232,0.20), transparent 66%)",
        },
    },
    {
        sx: {
            position: "absolute",
            top: "34%",
            right: "14%",
            width: 150,
            height: 150,
            background: "rgba(82,140,214,0.55)",
            borderRadius: "76% 24% 76% 24% / 24% 76% 24% 76%",
            animation: "lendiaFloat 9s ease-in-out infinite",
        },
    },
    {
        sx: {
            position: "absolute",
            top: "44%",
            right: "8%",
            width: 96,
            height: 96,
            background: "rgba(163,212,232,0.42)",
            borderRadius: "24% 76% 24% 76% / 76% 24% 76% 24%",
            animation: "lendiaFloat 7s ease-in-out infinite",
            animationDelay: "-2s",
        },
    },
    {
        sx: {
            position: "absolute",
            top: "20%",
            left: "16%",
            width: 54,
            height: 54,
            border: "2px solid rgba(163,212,232,0.35)",
            borderRadius: "50%",
            animation: "lendiaFloat 8s ease-in-out infinite",
            animationDelay: "-1s",
        },
    },
    {
        sx: {
            position: "absolute",
            bottom: "26%",
            right: "30%",
            width: 26,
            height: 26,
            background: "rgba(163,212,232,0.5)",
            borderRadius: "70% 30% 70% 30% / 30% 70% 30% 70%",
            animation: "lendiaFloat 6s ease-in-out infinite",
            animationDelay: "-3s",
        },
    },
];
